import apiClient from "@/app/api/client";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { endpoints } from "@/shared/api/endpoints";
import { unwrapResponse } from "@/shared/utils/response";
import type { BackendLeadStatus, BackendLeadStatusListResponse } from "../types/lead.types";
import { mapBackendLeadStatus } from "./leads.mapper";

type BackendSuccessResponse<T> = {
	success: true;
	data: T;
};

function resolveLeadStatusOrgId(explicitOrgId?: string) {
	if (explicitOrgId) {
		return explicitOrgId;
	}

	const { user } = useAuthStore.getState();
	return user?.orgId;
}

export type CreateLeadStatusData = {
	nameUz: string;
	nameRu?: string;
	color: string;
	indexOrder?: number;
	buildingId?: string;
	orgId?: string;
};

export type UpdateLeadStatusData = Partial<CreateLeadStatusData>;

export async function getLeadStatuses(buildingId?: string) {
	const params = buildingId ? { buildingId } : undefined;
	const response = await apiClient
		.get<BackendLeadStatusListResponse>(endpoints.leadStatuses.list, {
			params,
		})
		.catch((error) => {
			console.error("[LeadStatusesApi] Failed to fetch lead statuses:", error);
			throw error;
		});

	return response.data.data.map(mapBackendLeadStatus);
}

export async function createLeadStatus(data: CreateLeadStatusData) {
	const orgId = resolveLeadStatusOrgId(data.orgId);
	const response = await apiClient
		.post<BackendSuccessResponse<BackendLeadStatus> | BackendLeadStatus>(
			endpoints.leadStatuses.create,
			{
				...data,
				...(orgId ? { orgId } : {}),
			}
		)
		.catch((error) => {
			console.error("[LeadStatusesApi] Failed to create lead status:", error);
			throw error;
		});
	return mapBackendLeadStatus(unwrapResponse(response.data));
}

export async function updateLeadStatusById(id: string, data: UpdateLeadStatusData) {
	const response = await apiClient
		.patch<BackendSuccessResponse<BackendLeadStatus> | BackendLeadStatus>(
			endpoints.leadStatuses.detail(id),
			data
		)
		.catch((error) => {
			console.error("[LeadStatusesApi] Failed to update lead status:", error);
			throw error;
		});
	return mapBackendLeadStatus(unwrapResponse(response.data));
}

export async function deleteLeadStatus(id: string) {
	await apiClient.delete(endpoints.leadStatuses.detail(id)).catch((error) => {
		console.error("[LeadStatusesApi] Failed to delete lead status:", error);
		throw error;
	});
}
